import React, { useEffect, useRef, useState, useCallback } from "react";
import { L } from "@/lib/i18n";
import { Viewer } from "@photo-sphere-viewer/core";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import { GyroscopePlugin } from "@photo-sphere-viewer/gyroscope-plugin";
import { StereoPlugin } from "@photo-sphere-viewer/stereo-plugin";
import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import { ZoomIn, ZoomOut, Maximize2, Minimize2, Compass, Glasses } from "lucide-react";

/**
 * PanoramaScene — Phase 4 "Pro Panorama 360".
 *
 * Photo Sphere Viewer wrapped in React with a premium, self-styled control
 * bar (no PSV navbar). Adds on top of the Phase-1 MVP:
 *   - Zoom in / out buttons (+ mouse-wheel / pinch handled by PSV).
 *   - Fullscreen toggle (fullscreens our own wrapper so the controls +
 *     hotspots stay visible; PSV auto-resizes via its ResizeObserver).
 *   - Gyroscope (device-motion) mode on touch devices — pans the panorama
 *     as the phone moves. iOS permission is requested by PSV on first start.
 *   - VR / stereo (cardboard) mode — split-screen stereo view; requires the
 *     gyroscope API, so it only shows on capable touch devices.
 *   - Custom premium hotspot markers linked to units/scenes (unchanged).
 *
 * Props:
 *   - scene, hotspots, lang, defaultLang, onHotspot, onReady, rtl
 */
export default function PanoramaScene({ scene, hotspots = [], lang, defaultLang, onHotspot, onReady, rtl = false }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const onHotspotRef = useRef(onHotspot);

  const [ready, setReady] = useState(false);
  const [gyroOn, setGyroOn] = useState(false);
  const [vrOn, setVrOn] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Gyro/VR only make sense on touch devices exposing the orientation API,
  // and both plugins require HTTPS (which our preview + prod both are).
  const gyroCapable =
    typeof window !== "undefined" &&
    "DeviceOrientationEvent" in window &&
    ("ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0);
  const fullscreenCapable =
    typeof document !== "undefined" && !!document.fullscreenEnabled;

  useEffect(() => { onHotspotRef.current = onHotspot; }, [onHotspot]);

  // Instantiate PSV once per scene change (see Phase-1 note: a clean re-mount
  // is simpler than mutating an existing viewer's panorama + markers).
  useEffect(() => {
    if (!scene?.media_url || !containerRef.current) return;
    let mounted = true;
    setReady(false);
    setGyroOn(false);
    setVrOn(false);

    const initialMarkers = buildMarkers(hotspots || [], (k) => trWith(k, lang, defaultLang), L(lang, "info"));

    const viewer = new Viewer({
      container: containerRef.current,
      panorama: scene.media_url,
      defaultYaw: scene.panorama_initial_yaw ?? 0,
      defaultPitch: scene.panorama_initial_pitch ?? 0,
      defaultZoomLvl: yawPitchFovToZoom(scene.panorama_initial_fov ?? 60),
      navbar: false,
      mousewheel: true,
      keyboard: false,
      touchmoveTwoFingers: false,
      loadingTxt: "",
      plugins: [
        [MarkersPlugin, { markers: initialMarkers }],
        GyroscopePlugin.withConfig({ touchmove: true, moveMode: "smooth", roll: false }),
        StereoPlugin,
      ],
    });
    viewerRef.current = viewer;

    const markersPlugin = viewer.getPlugin(MarkersPlugin);
    const gyroPlugin = viewer.getPlugin(GyroscopePlugin);
    const stereoPlugin = viewer.getPlugin(StereoPlugin);

    viewer.addEventListener("ready", () => { if (mounted) { setReady(true); onReady?.(); } }, { once: true });

    markersPlugin?.addEventListener("select-marker", ({ marker }) => {
      const h = marker?.config?.data?.hotspot || marker?.data?.hotspot;
      if (h) onHotspotRef.current?.(h);
    });

    const onGyro = (e) => { if (mounted) setGyroOn(!!e.gyroscopeEnabled); };
    const onStereo = (e) => { if (mounted) setVrOn(!!e.stereoEnabled); };
    gyroPlugin?.addEventListener("gyroscope-updated", onGyro);
    stereoPlugin?.addEventListener("stereo-updated", onStereo);

    return () => {
      mounted = false;
      gyroPlugin?.removeEventListener("gyroscope-updated", onGyro);
      stereoPlugin?.removeEventListener("stereo-updated", onStereo);
      try { viewer.destroy(); } catch (_) { /* ignore */ }
      viewerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.id, scene?.media_url]);

  // Push hotspot list mutations into the marker plugin.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const plugin = viewer.getPlugin(MarkersPlugin);
    if (!plugin) return;
    const markers = buildMarkers(hotspots || [], (k) => trWith(k, lang, defaultLang), L(lang, "info"));
    try { plugin.setMarkers(markers); } catch (e) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspots, lang, defaultLang]);

  // Track fullscreen state on our wrapper element.
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(document.fullscreenElement === wrapperRef.current);
      // Nudge PSV to re-measure after the fullscreen transition settles.
      setTimeout(() => { try { viewerRef.current?.resize(); } catch (_) {} }, 120);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const zoomIn = useCallback(() => { try { viewerRef.current?.zoomIn(10); } catch (_) {} }, []);
  const zoomOut = useCallback(() => { try { viewerRef.current?.zoomOut(10); } catch (_) {} }, []);
  const toggleGyro = useCallback(() => { try { viewerRef.current?.getPlugin(GyroscopePlugin)?.toggle(); } catch (_) {} }, []);
  const toggleVr = useCallback(() => { try { viewerRef.current?.getPlugin(StereoPlugin)?.toggle(); } catch (_) {} }, []);
  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) document.exitFullscreen?.();
      else el.requestFullscreen?.();
    } catch (_) { /* ignore */ }
  }, []);

  const side = rtl ? "left-4" : "right-4";

  return (
    <div ref={wrapperRef} className="absolute inset-0 bg-black" data-testid="panorama-scene">
      <div ref={containerRef} className="absolute inset-0" />

      {ready && !vrOn && (
        <div
          className={`absolute bottom-4 ${side} z-30 flex flex-col gap-1.5 p-1.5 rounded-full pill-nav`}
          data-testid="panorama-controls"
          style={{ paddingBottom: "max(6px, env(safe-area-inset-bottom))" }}
        >
          <PanoBtn onClick={zoomIn} label={L(lang, "zoom_in")} testid="panorama-zoom-in"><ZoomIn className="h-4 w-4" /></PanoBtn>
          <PanoBtn onClick={zoomOut} label={L(lang, "zoom_out")} testid="panorama-zoom-out"><ZoomOut className="h-4 w-4" /></PanoBtn>
          {gyroCapable && (
            <PanoBtn onClick={toggleGyro} label={L(lang, "motion_control")} active={gyroOn} testid="panorama-gyro">
              <Compass className="h-4 w-4" />
            </PanoBtn>
          )}
          {gyroCapable && (
            <PanoBtn onClick={toggleVr} label={L(lang, "vr_mode")} active={vrOn} testid="panorama-vr">
              <Glasses className="h-4 w-4" />
            </PanoBtn>
          )}
          {fullscreenCapable && (
            <PanoBtn onClick={toggleFullscreen} label={L(lang, isFullscreen ? "exit_fullscreen" : "fullscreen")} active={isFullscreen} testid="panorama-fullscreen">
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </PanoBtn>
          )}
        </div>
      )}
    </div>
  );
}

function PanoBtn({ children, onClick, label, active = false, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={`w-9 h-9 rounded-full inline-flex items-center justify-center transition
        ${active
          ? "bg-[#C4A47C] text-black shadow-[0_0_18px_rgba(196,164,124,0.6)]"
          : "text-white/80 hover:text-white hover:bg-white/10"}`}
    >
      {children}
    </button>
  );
}

function trWith(obj, lang, defaultLang) {
  return (key) => {
    if (!obj) return undefined;
    const t = obj.translations || {};
    return t?.[key]?.[lang] ?? t?.[key]?.[defaultLang] ?? obj[key];
  };
}

function buildMarkers(hotspots, mkTr, fallbackLabel) {
  return hotspots.map((h) => {
    const tr = mkTr(h);
    const label = tr("label") || "";
    return {
      id: h.id,
      position: { yaw: h.yaw || 0, pitch: h.pitch || 0 },
      html: hotspotHtml(label || fallbackLabel),
      size: { width: 28, height: 28 },
      anchor: "center center",
      tooltip: label || undefined,
      data: { hotspot: h, description: tr("description") || "" },
    };
  });
}

function yawPitchFovToZoom(fovDeg) {
  const z = ((90 - Math.max(20, Math.min(120, fovDeg))) / 60) * 100;
  return Math.max(0, Math.min(100, z));
}

function hotspotHtml(label) {
  const escaped = String(label || "360°").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
  return (
    `<div class="psv-hotspot-dot" role="button" aria-label="${escaped}">
       <span class="psv-hotspot-halo"></span>
       <span class="psv-hotspot-core"></span>
     </div>`
  );
}
